import { useRef, useState, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { cn } from '@/lib/utils';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import {
  NextOperationalPeriodPickers,
  type NextPeriodPickersRef,
} from 'components/IncidentConfig/OperationalPeriodModal';
import { useCurrentPeriod } from 'components/IAP/hooks/incidentHooks';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { icsFormInputsKeys } from 'components/IAP/hooks/icsFormInputsHooks';
import { useOperationalPeriodWindow } from 'components/IAP/hooks/useOperationalPeriodWindow';
import { useOptionalICSContext } from 'components/IAP/planningView/ICSContext';
import { NULL_VALUE } from 'constants/displayConstants';

const toIsoOrNull = (value: Date | string | null | undefined): string | null =>
  value && dayjs(value).isValid() ? dayjs(value).toISOString() : null;

function WorkspaceNextPeriodModal({ onClose }: { onClose: () => void }) {
  const pickersRef = useRef<NextPeriodPickersRef>(null);
  const ics = useOptionalICSContext();
  const currPeriod = useCurrentPeriod();
  const { data: incidentInfo, isSuccess: incidentInfoReady } =
    useIncidentInfo();
  const saveNext = useUpdateIcsInfo();
  const queryClient = useQueryClient();
  const canEditIncident = !!useSelector(
    (state: unknown) =>
      (
        state as {
          app: {
            validateRBACPermissionForActionResult?: Record<string, boolean>;
          };
        }
      ).app.validateRBACPermissionForActionResult?.['incident:create']
  );

  const nextStartIso = toIsoOrNull(
    incidentInfo?.nextOperationalPeriodStartDateTime
  );
  const nextEndIso = toIsoOrNull(
    incidentInfo?.nextOperationalPeriodEndDateTime
  );
  const suggestedStartIso = toIsoOrNull(
    currPeriod.currentPeriod?.anticipated_end_date_time
  );
  const showingRecordedPeriod = ics != null && !ics.isWorking;

  const handleSave = async () => {
    if (!canEditIncident) return;
    const snapshot = pickersRef.current?.getValues();
    if (!snapshot) return;
    try {
      await saveNext.mutateAsync({
        nextOperationalPeriodStartDateTime: snapshot.startIso
          ? new Date(snapshot.startIso)
          : null,
        nextOperationalPeriodEndDateTime: snapshot.endIso
          ? new Date(snapshot.endIso)
          : null,
      });
      toast.success('Operational period saved');
      void queryClient.invalidateQueries({ queryKey: icsFormInputsKeys.all });
      onClose();
    } catch {
      // The mutation hook already surfaces the error.
    }
  };

  return (
    <CustomModal
      show
      heading="This incident workspace’s next operational period"
      onClose={onClose}
      onConfirm={() => {
        void handleSave();
      }}
      saveButton="Save changes"
      buttonDisable={
        !canEditIncident || saveNext.isPending || !incidentInfoReady
      }
    >
      <p className="mb-3">
        This updates the scheduled start and end of the next operational period
        for this incident workspace. The working period’s ICS forms print this
        window.
      </p>
      {showingRecordedPeriod ? (
        <p className="text-weak small mb-3">
          This form is showing a recorded window. Saving changes the workspace’s
          next period.
        </p>
      ) : null}
      <NextOperationalPeriodPickers
        ref={pickersRef}
        key={`${nextStartIso ?? 'none'}-${nextEndIso ?? 'none'}`}
        suggestedStartIso={suggestedStartIso}
        startIso={nextStartIso}
        endIso={nextEndIso}
        disabled={!canEditIncident || saveNext.isPending || !incidentInfoReady}
      />
    </CustomModal>
  );
}

/** Click target shared by every Operational Period box. */
export function OperationalPeriodScheduleTrigger({
  children,
  testId,
  className,
}: {
  children: ReactNode;
  testId?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        title="Edit the incident workspace operational period"
        className={cn(
          'tw:block tw:w-full tw:min-w-0 tw:cursor-pointer tw:appearance-none tw:border-0 tw:bg-transparent tw:p-0 tw:text-left tw:font-inherit tw:break-words tw:text-foreground',
          className
        )}
        onClick={() => setOpen(true)}
      >
        {children}
      </button>
      {open ? (
        <WorkspaceNextPeriodModal onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

/** The single-line operational period window, opened into the workspace modal. */
export function OperationalPeriodScheduleControl({
  testId,
}: {
  testId: string;
}) {
  const { range } = useOperationalPeriodWindow();

  return (
    <OperationalPeriodScheduleTrigger testId={testId}>
      {range.trim() ? range : NULL_VALUE}
    </OperationalPeriodScheduleTrigger>
  );
}
